import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  createPositionsNav,
  createScopedNav,
} from "../../common/slices/specsEditor/helpers";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import type { SpecsModel } from "./specsModel/SpecsModel";
import type {
  SpecsEditorNav,
  SpecsEditorSession,
  SpecsLayer,
  SpecsLayerModelPath,
} from "./types";
import { useSpecsEditorLayerModelPaths } from "./useSpecsEditorLayerModelPaths";

type StyleNav = Extract<
  SpecsEditorNav,
  { kind: "element" } | { kind: "layer" } | { kind: "positions" }
>;

type UseSpecsEditorStylesArgs = {
  currentVersion: ReturnType<SpecsModel["getVersion"]> | null;
  layers: SpecsLayer[];
  selectedNav: SpecsEditorSession["nav"];
  session: SpecsEditorSession;
};

function getNavForStyle(
  nav: SpecsEditorNav,
  elementName: string,
  styleName: string,
  partName?: string
): StyleNav | null {
  switch (nav.kind) {
    case "element":
    case "layer":
    case "positions":
      return nav.element === elementName &&
        nav.style === styleName &&
        (!partName || nav.part === partName)
        ? nav
        : null;
    default:
      return null;
  }
}

function layerPathMatchesStyle(args: {
  path: SpecsLayerModelPath | null;
  elementName: string;
  styleName: string;
  partName?: string;
}) {
  const { path, elementName, styleName, partName } = args;

  if (!path || path.elementName !== elementName) {
    return false;
  }

  if (path.styleName !== styleName) {
    return false;
  }

  return !partName || path.partName === partName;
}

function clearLayerStyle(args: {
  path: SpecsLayerModelPath | null;
  elementName: string;
  styleName: string;
  partName?: string;
}) {
  const path = args.path;
  if (!path || !layerPathMatchesStyle(args)) {
    return path;
  }

  return { ...path, styleName: "" };
}

function renameLayerStyle(args: {
  path: SpecsLayerModelPath | null;
  elementName: string;
  previousStyleName: string;
  nextStyleName: string;
  partName?: string;
}) {
  const { path, elementName, previousStyleName, nextStyleName, partName } =
    args;
  const matchesStyle = layerPathMatchesStyle({
    path,
    elementName,
    styleName: previousStyleName,
    partName,
  });

  if (!path || !matchesStyle) {
    return path;
  }

  return { ...path, styleName: nextStyleName };
}

function createNavForRenamedStyle(args: {
  nav: SpecsEditorNav;
  session: SpecsEditorSession;
  elementName: string;
  previousStyleName: string;
  nextStyleName: string;
  partName?: string;
}) {
  const {
    nav,
    session,
    elementName,
    previousStyleName,
    nextStyleName,
    partName,
  } = args;

  const scopedNav = getNavForStyle(
    nav,
    elementName,
    previousStyleName,
    partName
  );
  if (!scopedNav) {
    return null;
  }

  if (scopedNav.kind === "positions") {
    return createPositionsNav(session, {
      positionId: scopedNav.positionId,
      partName: scopedNav.part,
      elementName: scopedNav.element,
      styleName: nextStyleName,
    });
  }

  return createScopedNav(
    session,
    scopedNav.part,
    scopedNav.element,
    nextStyleName
  );
}

export function useSpecsEditorStyles({
  currentVersion,
  layers,
  selectedNav,
  session,
}: UseSpecsEditorStylesArgs) {
  const dispatch = useDispatch();
  const updateLayerModelPaths = useSpecsEditorLayerModelPaths(layers);

  const setActiveStyle = useCallback(
    (partName: string, elementName: string, styleName: string) => {
      currentVersion?.setActiveStyle(partName, elementName, styleName);
      dispatch(
        specsEditorActions.setActiveStyle({
          partName,
          elementName,
          styleName,
        })
      );
    },
    [currentVersion, dispatch]
  );

  const addStyle = useCallback(
    (elementName: string, styleName: string, partName?: string) => {
      currentVersion
        ?.getElement(elementName, partName)
        ?.element.addStyle(styleName);
    },
    [currentVersion]
  );

  const removeStyle = useCallback(
    (elementName: string, styleName: string, partName?: string) => {
      currentVersion
        ?.getElement(elementName, partName)
        ?.element.removeStyle(styleName);
      updateLayerModelPaths((path) =>
        clearLayerStyle({ path, elementName, styleName, partName })
      );
    },
    [currentVersion, updateLayerModelPaths]
  );

  const updateStyle = useCallback(
    (
      elementName: string,
      previousStyleName: string,
      nextStyleName: string,
      partName?: string
    ) => {
      currentVersion
        ?.getElement(elementName, partName)
        ?.element.renameStyle(previousStyleName, nextStyleName);
      updateLayerModelPaths((path) =>
        renameLayerStyle({
          path,
          elementName,
          previousStyleName,
          nextStyleName,
          partName,
        })
      );
      const nextNav = createNavForRenamedStyle({
        nav: selectedNav,
        session,
        elementName,
        previousStyleName,
        nextStyleName,
        partName,
      });

      if (nextNav) {
        dispatch(specsEditorActions.setNav(nextNav));
      }
    },
    [currentVersion, dispatch, selectedNav, session, updateLayerModelPaths]
  );

  const updateStyleIcon = useCallback(
    (
      elementName: string,
      styleName: string,
      menuIconUrl: string,
      partName?: string
    ) => {
      currentVersion
        ?.getElement(elementName, partName)
        ?.element.setStyleIcon(styleName, menuIconUrl);
    },
    [currentVersion]
  );

  return {
    setActiveStyle,
    addStyle,
    removeStyle,
    updateStyle,
    updateStyleIcon,
  };
}
